import type { AnyRouter } from '@tanstack/react-router';

/**
 * The pathname from the load immediately before the current load, recorded app-wide. Internal
 * redirect hops count as loads, even when their URL was never rendered. Survives route component
 * unmounts (it's module-level, fed by a single router subscription), so a list page that remounts
 * on return can tell whether it was re-entered fresh or returned-to from one of its own sub-routes
 * (e.g. a record detail). Used by saved-views to decide whether a built-in preset should persist on
 * return.
 */
let previousPathname = '';
let currentPathname = '';

/**
 * Subscribe once (from the router provider) to keep a rolling [previous, current] loaded pathname.
 * Derives `previous` from the last `current` on each load (keyed on `toLocation`, which is always
 * populated) rather than reading `fromLocation` — `fromLocation` is unreliable on browser
 * back/forward (history pops), which would make a return-via-back look like a fresh entry. Listens
 * on `onBeforeLoad` rather than `onBeforeNavigate` because the router stops emitting the latter
 * after the first `throw redirect()` of the session (see `trackNavigationSideEffects` in
 * navigation-side-effects.ts). Pathname-only changes are ignored so a same-page search/filter
 * update doesn't clobber `previous`.
 */
export function trackPreviousPathname(router: AnyRouter): () => void {
    currentPathname = router.state?.location?.pathname ?? currentPathname;
    return router.subscribe('onBeforeLoad', (event) => {
        const to = event.toLocation?.pathname;
        if (!to || to === currentPathname) return;
        previousPathname = currentPathname;
        currentPathname = to;
    });
}

export function getPreviousPathname(): string {
    return previousPathname;
}

/** Test-only reset of the module-level tracker. */
export function __resetPreviousPathnameForTests(previous = '', current = ''): void {
    previousPathname = previous;
    currentPathname = current;
}

/**
 * Whether `previousPathname` is a descendant of `listPathname` — i.e. we're returning to the list
 * from one of its own sub-routes (e.g. `/console/1/claims/details/381/manage` under
 * `/console/1/claims`) rather than entering it fresh from elsewhere (home, another section). A
 * trailing slash on the list path is tolerated, and a sibling that merely shares a prefix
 * (`/console/1/claims-products`) does NOT match because the boundary `/` is required.
 */
export function isReturnFromSubRoute(previousPathname: string, listPathname: string): boolean {
    if (!previousPathname || !listPathname) return false;
    const base = listPathname.replace(/\/+$/, '');
    return previousPathname.startsWith(`${base}/`);
}
